import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Profile = ({ user: currentUser, refreshUser }: any) => {
  const { username } = useParams<{ username?: string }>();
  const isOwnProfile = !username || (currentUser && currentUser.username === username);
  const targetUsername = username || (currentUser && currentUser.username);

  const [profile, setProfile] = useState<any>(null);
  const [sharedSpells, setSharedSpells] = useState([]);
  const [sharedGrimoire, setSharedGrimoire] = useState([]);
  const [sharedJournal, setSharedJournal] = useState([]);
  const [activeTab, setActiveTab] = useState<'spells' | 'grimoire' | 'journal'>('spells');
  const [isEditing, setIsEditing] = useState(false);
  
  // Profile fields
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [profilePicture, setProfilePicture] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  
  // Password fields
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState({ text: '', type: '' });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        if (isOwnProfile && !username) {
          const res = await axios.get(`${API_URL}/profile`, {
            headers: { Authorization: `Bearer ${currentUser.token}` }
          });
          setProfile(res.data);
          setDisplayName(res.data.display_name || '');
          setBio(res.data.bio || '');
          setProfilePicture(res.data.profile_picture || '');
          setFirstName(res.data.first_name || '');
          setLastName(res.data.last_name || '');
          setEmail(res.data.email || '');
        } else {
          const res = await axios.get(`${API_URL}/profile/${targetUsername}`);
          setProfile(res.data.profile);
          setDisplayName(res.data.profile.display_name || '');
          setBio(res.data.profile.bio || '');
          setProfilePicture(res.data.profile.profile_picture || '');
          setFirstName(res.data.profile.first_name || '');
          setLastName(res.data.profile.last_name || '');
          setSharedSpells(res.data.shared_spells);
          setSharedGrimoire(res.data.shared_grimoire);
          setSharedJournal(res.data.shared_journal);
        }
      } catch (err) {
        console.error('Error fetching profile', err);
      } finally {
        setLoading(false);
      }
    };

    if (targetUsername) fetchProfile();
  }, [targetUsername, isOwnProfile, currentUser, username]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicture(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API_URL}/profile`, 
        { display_name: displayName, bio, profile_picture: profilePicture, first_name: firstName, last_name: lastName, email },
        { headers: { Authorization: `Bearer ${currentUser.token}` } }
      );
      setProfile({ ...profile, display_name: displayName, bio, profile_picture: profilePicture, first_name: firstName, last_name: lastName, email });
      setIsEditing(false);
      if (refreshUser) refreshUser();
    } catch (err) {
      console.error('Error updating profile', err);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ text: "Passwords do not match", type: 'error' });
      return;
    }
    try {
      await axios.put(`${API_URL}/profile/password`, 
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${currentUser.token}` } }
      );
      setPasswordMessage({ text: "Password updated successfully", type: 'success' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        setIsChangingPassword(false);
        setPasswordMessage({ text: '', type: '' });
      }, 2000);
    } catch (err: any) {
      setPasswordMessage({ text: err.response?.data?.error || "Error updating password", type: 'error' });
    }
  };

  if (loading) return <LoadingSpinner message="Invoking profile data..." />;
  if (!profile) return <div className="content"><p>Spirit not found.</p></div>;

  return (
    <div className="content">
      <div className="card profile-header" style={{ textAlign: 'center' }}>
        <div className="profile-pic-container" style={{ marginBottom: '1rem' }}>
          {profilePicture || profile.profile_picture ? (
            <img 
              src={profilePicture || profile.profile_picture} 
              alt={profile.username} 
              style={{ width: '150px', height: '150px', borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--gold)' }} 
            />
          ) : (
            <div style={{ width: '150px', height: '150px', borderRadius: '50%', background: 'var(--mystic-purple)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', border: '3px solid var(--gold)' }}>
              {profile.username[0].toUpperCase()}
            </div>
          )}
        </div>

        {isEditing ? (
          <div style={{ maxWidth: '400px', margin: '0 auto', textAlign: 'left' }}>
            <label>Profile Picture</label>
            <input type="file" accept="image/*" onChange={handleImageChange} style={{ marginBottom: '1rem' }} />
            
            <label>First Name</label>
            <input 
              type="text" 
              placeholder="First Name" 
              value={firstName} 
              onChange={e => setFirstName(e.target.value)} 
              style={{ width: '100%', marginBottom: '1rem' }}
            />
            
            <label>Last Name</label>
            <input 
              type="text" 
              placeholder="Last Name" 
              value={lastName} 
              onChange={e => setLastName(e.target.value)} 
              style={{ width: '100%', marginBottom: '1rem' }}
            />

            <label>Email Address</label>
            <input 
              type="email" 
              placeholder="Email Address" 
              value={email} 
              onChange={e => setEmail(e.target.value)} 
              style={{ width: '100%', marginBottom: '1rem' }}
            />

            <label>Display Name</label>
            <input 
              type="text" 
              placeholder="Display Name (e.g. High Priestess)" 
              value={displayName} 
              onChange={e => setDisplayName(e.target.value)} 
              style={{ width: '100%', marginBottom: '1rem' }}
            />
            
            <label>Bio</label>
            <textarea 
              placeholder="Tell the circle about your path..." 
              value={bio} 
              onChange={e => setBio(e.target.value)} 
              rows={4}
              style={{ width: '100%', marginBottom: '1rem' }}
            />
            <div style={{ textAlign: 'center' }}>
              <button onClick={handleSave}>Save Path</button>
              <button onClick={() => setIsEditing(false)} style={{ background: 'transparent', color: 'var(--gold)', border: '1px solid var(--gold)', marginLeft: '0.5rem' }}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <h2>{profile.display_name || profile.username}</h2>
            {profile.display_name && <p style={{ color: 'var(--gold)', marginTop: '-1rem' }}>@{profile.username}</p>}
            <p style={{ fontStyle: 'italic', color: '#ccc', marginBottom: '1rem' }}>{profile.bio || 'A mysterious practitioner.'}</p>
            {isOwnProfile && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                <button onClick={() => setIsEditing(true)}>Edit Profile</button>
                <button onClick={() => setIsChangingPassword(true)} style={{ background: 'transparent', color: 'var(--gold)', border: '1px solid var(--gold)' }}>Security Settings</button>
              </div>
            )}
            {!isOwnProfile && currentUser && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                <Link to={`/messages/${profile.username}`} className="btn-gold" style={{ textDecoration: 'none', padding: '0.5rem 1.5rem' }}>
                  ✉️ Send Message
                </Link>
              </div>
            )}
          </>
        )}

        {isOwnProfile && isChangingPassword && (
          <div className="card" style={{ marginTop: '2rem', maxWidth: '400px', margin: '2rem auto 0 auto', border: '1px solid var(--gold)' }}>
            <h3>Change Secret Key</h3>
            {passwordMessage.text && <p style={{ color: passwordMessage.type === 'error' ? 'red' : 'var(--gold)' }}>{passwordMessage.text}</p>}
            <form onSubmit={handlePasswordChange}>
              <input type="password" placeholder="Current Password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required />
              <input type="password" placeholder="New Password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required />
              <input type="password" placeholder="Confirm New Password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
              <div style={{ marginTop: '1rem' }}>
                <button type="submit">Update Password</button>
                <button type="button" onClick={() => setIsChangingPassword(false)} style={{ background: 'transparent', color: 'var(--gold)', border: 'none', marginLeft: '1rem' }}>Cancel</button>
              </div>
            </form>
          </div>
        )}
      </div>

      {!isOwnProfile && (
        <div style={{ marginTop: '2rem' }}>
          <div className="tab-container" style={{ marginBottom: '2rem' }}>
            <div 
              className={`tab ${activeTab === 'spells' ? 'active' : ''}`} 
              onClick={() => setActiveTab('spells')}
            >
              SHARED SPELLS
            </div>
            <div 
              className={`tab ${activeTab === 'grimoire' ? 'active' : ''}`} 
              onClick={() => setActiveTab('grimoire')}
            >
              SHARED GRIMOIRE
            </div>
            <div 
              className={`tab ${activeTab === 'journal' ? 'active' : ''}`} 
              onClick={() => setActiveTab('journal')}
            >
              SHARED JOURNAL
            </div>
          </div>

          <div className="grid">
            {activeTab === 'spells' && (
              sharedSpells.length > 0 ? sharedSpells.map((spell: any) => (
                <div key={spell.id} className="card">
                  <h3>{spell.name}</h3>
                  <p><strong>Intent:</strong> {spell.intent}</p>
                  <p><strong>Timing:</strong> {spell.timing}</p>
                  <Link to={`/public/spells/${spell.id}`} className="btn-gold" style={{ display: 'inline-block', marginTop: '1rem', textDecoration: 'none', fontSize: '0.8rem', padding: '0.3rem 0.8rem' }}>View Discussion</Link>
                </div>
              )) : <p>No shared spells yet.</p>
            )}

            {activeTab === 'grimoire' && (
              sharedGrimoire.length > 0 ? sharedGrimoire.map((entry: any) => {
                const strippedContent = entry.content.replace(/<[^>]*>?/gm, '');
                return (
                  <div key={entry.id} className="card">
                    <span className="badge">{entry.category}</span>
                    <h3>{entry.title}</h3>
                    <p>{strippedContent.substring(0, 100)}...</p>
                    <Link to={`/public/grimoire/${entry.id}`} className="btn-gold" style={{ display: 'inline-block', marginTop: '1rem', textDecoration: 'none', fontSize: '0.8rem', padding: '0.3rem 0.8rem' }}>View Discussion</Link>
                  </div>
                );
              }) : <p>No shared grimoire entries yet.</p>
            )}

            {activeTab === 'journal' && (
              sharedJournal.length > 0 ? sharedJournal.map((entry: any) => {
                const strippedContent = entry.content.replace(/<[^>]*>?/gm, '');
                return (
                  <div key={entry.id} className="card">
                    <small>{entry.date}</small>
                    <h3>Journal Entry</h3>
                    <p>{strippedContent.substring(0, 100)}...</p>
                    <Link to={`/public/journal/${entry.id}`} className="btn-gold" style={{ display: 'inline-block', marginTop: '1rem', textDecoration: 'none', fontSize: '0.8rem', padding: '0.3rem 0.8rem' }}>View Discussion</Link>
                  </div>
                );
              }) : <p>No shared journal entries yet.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
